import type { RefObject } from "react";
import { useEffect } from "react";
import type { AuthSession } from "@/lib/backend";
import type { AuthErrorReason, StaffSessionOrigin } from "../types";

export function getSmsTokenExpiryDelay(expiresAt: number | null): number | null {
  if (typeof expiresAt !== "number" || !Number.isFinite(expiresAt)) {
    return null;
  }
  const delay = expiresAt * 1000 - Date.now();
  return delay > 0 ? delay : null;
}

export function isSmsTokenExpired(expiresAt: number | null): boolean {
  return getSmsTokenExpiryDelay(expiresAt) === null;
}

export function useSmsSessionExpiry({
  authGenerationRef,
  clearAuthState,
  originRef,
  session,
}: {
  readonly authGenerationRef: RefObject<number>;
  readonly clearAuthState: (reason: AuthErrorReason | null) => void;
  readonly originRef: RefObject<StaffSessionOrigin | null>;
  readonly session: AuthSession | null;
}): void {
  useEffect(() => {
    if (originRef.current !== "sms") {
      return;
    }
    const delay = getSmsTokenExpiryDelay(session?.expires_at ?? null);
    if (delay === null) {
      clearAuthState("session_invalid");
      return;
    }
    const generation = authGenerationRef.current;
    const timeoutId = window.setTimeout(() => {
      if (generation === authGenerationRef.current && originRef.current === "sms") {
        clearAuthState("session_invalid");
      }
    }, delay);
    return () => window.clearTimeout(timeoutId);
  }, [authGenerationRef, clearAuthState, originRef, session]);
}
